import { _ } from "../polyglot/polyglot.js";
import { caretDownIcon } from "../elements/icons/index.js";
import { widget } from "@foretagsplatsen/widgetjs";

/**
 * `exceptionsListWidget` is a widget responsible for rendering a list of
 * exceptions by type.
 *
 * @param {*} spec - Parameter object
 * @param {map} [spec.exceptions] - exceptions to display
 * @param {boolean} [spec.firstAlwaysShown] - If true, the first exceptions of
 *     each group is always displayed
 * @param {string} [spec.cssClass] - specific css class
 * @param {*} my - protected fields holder
 * @return {{}}
 */
function exceptionsListWidget(spec, my) {
	spec ||= {};
	my ||= {};

	const that = widget(spec, my);

	my.exceptions = spec.exceptions || {};
	my.firstAlwaysShown = spec.firstAlwaysShown;
	my.cssClass = spec.cssClass || "danger";

	that.setExceptions = function (exceptions) {
		my.exceptions = exceptions;
		that.update();
	};

	that.removeExceptions = function () {
		my.exceptions = {};
		that.update();
	};

	that.renderContentOn = function (html) {
		if (my.isEmpty()) {
			my.renderEmptyOn(html);
			return;
		}

		if (my.firstAlwaysShown) {
			html.render(
				my.exceptionGroups().map(my.exceptionWithFirstRenderer),
			);
		} else {
			html.render(my.exceptionGroups().map(my.exceptionGroupRenderer));
		}
	};

	my.renderEmptyOn = function (html) {
		html.div({ klass: "empty-exceptions-list" });
	};

	my.exceptionGroupRenderer = function (key) {
		const group = my.exceptions[key.toString()];

		return function (html) {
			let expandableRegion;
			let icon;

			html.div(
				{ klass: ["alert", `alert-${my.cssClass}`] },
				html.h4(
					{ klass: "nomargin alert-content-trigger" },
					_("{type} ({total})", {
						type: group.title,
						total: group.exceptions.length,
					}),
					(html) => {
						icon = html.span({
							class: [caretDownIcon.getCssClass(), "pull-right"],
						});
					},
				),
				(html) => {
					expandableRegion = html.div(
						{
							style: "display: none;",
							klass: "alert-content",
						},
						group.exceptions.map(my.messageRenderer),
					);
				},
			).click(my.toggleException(expandableRegion, icon));
		};
	};

	my.exceptionWithFirstRenderer = function (key) {
		const group = my.exceptions[key.toString()];
		const exceptions = group.exceptions.slice();

		// Removes the first element from an array and returns that element.
		// This method changes the length of the array.
		const firstException = exceptions.shift();

		return function (html) {
			let expandableRegion;
			let icon;

			html.div(
				{ klass: ["alert", `alert-${my.cssClass}`] },
				html.div(
					{
						klass: "nomargin alert-content-trigger",
						"data-test": "alert-exceptions",
					},
					html.h4(
						_("{type} ({total})", {
							type: group.title,
							total: exceptions.length + 1,
						}),
						(html) => {
							icon = html.span({
								klass: [
									caretDownIcon.getCssClass(),
									"pull-right",
								],
							});
						},
					),
					html.div(
						{ klass: "alert-content" },
						my.messageRenderer(firstException),
					),
				),
				(html) => {
					expandableRegion = html.div(
						{
							style: "display: none;",
							klass: "alert-content",
						},
						exceptions.map(my.messageRenderer),
					);
				},
			).click(my.toggleException(expandableRegion, icon));
		};
	};

	my.messageRenderer = function (exception) {
		const message = exception.getMessage
			? exception.getMessage()
			: exception.message;

		return function (html) {
			html.p(message);
		};
	};

	my.toggleException = function (expandableRegion, icon) {
		let collapsed = true;

		return function () {
			expandableRegion.asJQuery().slideToggle(300);
			collapsed = !collapsed;
			updateIcon(icon, collapsed);
		};
	};

	my.exceptionGroups = function () {
		return Object.keys(my.exceptions);
	};

	my.isEmpty = function () {
		return !my.exceptionGroups().length;
	};

	function updateIcon(icon, collapsed) {
		icon.removeClass("fa-caret-down");
		icon.removeClass("fa-caret-up");
		if (collapsed) {
			icon.addClass("fa-caret-down");
		} else {
			icon.addClass("fa-caret-up");
		}
	}

	return that;
}

export default exceptionsListWidget;
